import { create } from "zustand";

import type { BulkSelection } from "@/lib/api/model";
import type { ArbitratorStatus } from "../lib/types";

type BulkMode = "include" | "all";

/**
 * Lightweight summary of an arbitrator session that the job pipeline uses to
 * decide between "open setup sheet" and "navigate to results". The gate writes
 * this once per stage at layout-mount time so the click handler never has to
 * wait for a fetch.
 */
export interface SessionMeta {
  id: string;
  status: ArbitratorStatus;
  /** Number of active criteria attached to the session. */
  criteriaCount: number;
  /** True when the upstream search has changed since this session ran. */
  isStale: boolean;
  targetsTotal: number;
  targetsCompleted: number;
}

/**
 * Field-wise equality for SessionMeta (and the null "no session" sentinel).
 * The remember*SessionMeta actions build a fresh object every call; without
 * this guard set() always swaps in a new `sessionMetaBy*` reference, so
 * whole-object subscribers get a new useSyncExternalStore snapshot on every
 * write — and any writer whose effect dep transitively churns from that
 * re-render drives React #185 ("Maximum update depth exceeded"). Returning the
 * same state ref on a no-op lets React bail, mirroring the guards on
 * rememberJobStageSession/markSuggestTried.
 */
function sessionMetaEquals(
  a: SessionMeta | null | undefined,
  b: SessionMeta | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.status === b.status &&
    a.criteriaCount === b.criteriaCount &&
    a.isStale === b.isStale &&
    a.targetsTotal === b.targetsTotal &&
    a.targetsCompleted === b.targetsCompleted
  );
}

interface ViewState {
  /** profile_ids of expanded result rows. */
  expanded: Set<string>;
  /** profile_ids included when `bulkMode` is "include". */
  selected: Set<string>;
  /** profile_ids excluded when `bulkMode` is "all". */
  excluded: Set<string>;
  /** Virtual selection mode for arbitrator bulk actions. */
  bulkMode: BulkMode;
  /** Total rows matched by the virtual selection before exclusions. */
  selectionTotal: number;
  /** Optional score filter attached to virtual selection. */
  selectionMinScore: number | null;
  /** When set, the Review Profiles sheet opens for this profile_id. */
  reviewProfileId: string | null;
  /** When set alongside reviewProfileId, the sheet auto-activates citations for this criterion. */
  reviewCriterionId: string | null;

  /**
   * Job Arbitrator setup-sheet trigger. When set, the job layout's gate
   * opens the sheet for this `(jobId, stageId?)` pair — without navigating
   * away from whatever route the user is currently on. Cleared on close /
   * launch.
   */
  setupJob: { jobId: string; stageId: string | null } | null;

  /**
   * Cache of resolved session ids per job stage, keyed `${jobId}:${stageId}`.
   * Populated by whichever component first creates/fetches the session
   * (typically the layout-level gate). The /arbitrator page reads from here
   * so it doesn't have to fire its own duplicate create — single source of
   * truth, no race.
   */
  sessionIdByJobStage: Record<string, string>;

  /**
   * Richer session metadata, keyed `${jobId}:${stageId}`. Populated by the
   * gate after the by-stage lookup resolves. Empty entry (or absent key)
   * means "no session yet". `criteriaCount === 0` means a session exists but
   * has never been configured — clicking through should open the setup
   * sheet, NOT navigate to the (blank) results page.
   */
  sessionMetaByJobStage: Record<string, SessionMeta | null>;

  /**
   * Set of session ids the setup gate has already auto-suggested criteria for.
   * Keyed by sessionId so the "don't re-suggest" decision survives sheet
   * close/reopen and gate remount (a useRef did not). ARBI-323/325.
   */
  attemptedSuggestForSession: Record<string, true>;

  toggleExpanded: (profileId: string) => void;
  toggleSelected: (profileId: string) => void;
  clearSelected: () => void;
  selectAll: (ids: string[]) => void;
  selectAllResults: (count: number, minScore?: number | null) => void;
  mergeSelected: (ids: string[]) => void;
  getSelectedCount: () => number;
  getBulkSelection: () => BulkSelection | null;
  openReview: (profileId: string, criterionId?: string | null) => void;
  closeReview: () => void;
  clearReviewCriterion: () => void;
  openSetupForJob: (jobId: string, stageId?: string | null) => void;
  closeSetup: () => void;
  rememberJobStageSession: (
    jobId: string,
    stageId: string,
    sessionId: string,
  ) => void;
  rememberJobStageSessionMeta: (
    jobId: string,
    stageId: string,
    meta: SessionMeta | null,
  ) => void;
  markSuggestTried: (sessionId: string) => void;
  hasSuggestTried: (sessionId: string) => boolean;
}

export const useArbitratorViewStore = create<ViewState>((set, get) => ({
  expanded: new Set(),
  selected: new Set(),
  excluded: new Set(),
  bulkMode: "include",
  selectionTotal: 0,
  selectionMinScore: null,
  reviewProfileId: null,
  reviewCriterionId: null,
  setupJob: null,
  sessionIdByJobStage: {},
  sessionMetaByJobStage: {},
  attemptedSuggestForSession: {},
  toggleExpanded: (id) =>
    set((s) => {
      const next = new Set(s.expanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { expanded: next };
    }),
  toggleSelected: (id) =>
    set((s) => {
      if (s.bulkMode === "all") {
        const nextExcluded = new Set(s.excluded);
        if (nextExcluded.has(id)) nextExcluded.delete(id);
        else nextExcluded.add(id);
        return { excluded: nextExcluded };
      }

      const nextSelected = new Set(s.selected);
      if (nextSelected.has(id)) nextSelected.delete(id);
      else nextSelected.add(id);
      return { selected: nextSelected };
    }),
  clearSelected: () =>
    set({
      bulkMode: "include",
      selected: new Set(),
      excluded: new Set(),
      selectionTotal: 0,
      selectionMinScore: null,
    }),
  selectAll: (ids) =>
    set({
      bulkMode: "include",
      selected: new Set(ids),
      excluded: new Set(),
      selectionTotal: 0,
      selectionMinScore: null,
    }),
  selectAllResults: (count, minScore = null) =>
    set({
      bulkMode: "all",
      selected: new Set(),
      excluded: new Set(),
      selectionTotal: count,
      selectionMinScore: minScore,
    }),
  mergeSelected: (ids) =>
    set((s) => {
      // Un-exclude rather than rebuild, so merging a page into a plain
      // select-all does not shrink it down to just `ids`. A score-band
      // select-all can't express "…plus these below-threshold rows", so that
      // case still collapses to an explicit include set.
      if (s.bulkMode === "all" && s.selectionMinScore == null) {
        const nextExcluded = new Set(s.excluded);
        for (const id of ids) nextExcluded.delete(id);
        return { excluded: nextExcluded };
      }
      const next =
        s.bulkMode === "include" ? new Set(s.selected) : new Set<string>();
      for (const id of ids) next.add(id);
      return {
        bulkMode: "include",
        selected: next,
        excluded: new Set(),
        selectionTotal: 0,
        selectionMinScore: null,
      };
    }),
  getSelectedCount: () => {
    const state = get();
    if (state.bulkMode === "all") {
      return Math.max(state.selectionTotal - state.excluded.size, 0);
    }
    return state.selected.size;
  },
  getBulkSelection: () => {
    const state = get();
    if (state.bulkMode === "all") {
      return {
        mode: "all",
        exclude_ids: Array.from(state.excluded),
      };
    }
    if (state.selected.size === 0) return null;
    return {
      mode: "include",
      include_ids: Array.from(state.selected),
    };
  },
  openReview: (profileId, criterionId = null) =>
    set({ reviewProfileId: profileId, reviewCriterionId: criterionId ?? null }),
  closeReview: () => set({ reviewProfileId: null, reviewCriterionId: null }),
  clearReviewCriterion: () => set({ reviewCriterionId: null }),
  openSetupForJob: (jobId, stageId = null) =>
    set({ setupJob: { jobId, stageId } }),
  closeSetup: () => set({ setupJob: null }),
  rememberJobStageSession: (jobId, stageId, sessionId) =>
    set((s) => {
      const key = `${jobId}:${stageId}`;
      return s.sessionIdByJobStage[key] === sessionId
        ? s
        : {
            sessionIdByJobStage: {
              ...s.sessionIdByJobStage,
              [key]: sessionId,
            },
          };
    }),
  rememberJobStageSessionMeta: (jobId, stageId, meta) =>
    set((s) => {
      const key = `${jobId}:${stageId}`;
      return sessionMetaEquals(s.sessionMetaByJobStage[key], meta)
        ? s
        : {
            sessionMetaByJobStage: {
              ...s.sessionMetaByJobStage,
              [key]: meta,
            },
          };
    }),
  markSuggestTried: (sessionId) =>
    set((s) =>
      s.attemptedSuggestForSession[sessionId]
        ? s
        : {
            attemptedSuggestForSession: {
              ...s.attemptedSuggestForSession,
              [sessionId]: true,
            },
          },
    ),
  hasSuggestTried: (sessionId) => !!get().attemptedSuggestForSession[sessionId],
}));
